/**
 * System-role selector hooks — derive system-tier privileges from the auth
 * store without issuing a separate API call.
 *
 * A2 extended /v1/me to include `systemRole`, `isSystemAdmin`, `isSystemRoot`
 * on the User shape. These hooks read those fields from the Zustand auth store
 * that is hydrated once at app boot (see stores/useAuthStore.ts).
 *
 * Usage pattern (no memo needed for booleans — primitive equality prevents
 * unnecessary re-renders through Zustand's selector diffing):
 *   const isAdmin = useIsSystemAdmin(); // true for role 'admin' or 'root'
 *   const isRoot  = useIsSystemRoot();  // true only for role 'root'
 *   const role    = useSystemRole();    // 'root' | 'admin' | null
 */

import { useAuthStore } from "@/stores";
import type { SystemRole } from "../types";

/**
 * Returns the raw system role from the current user.
 * null when: user is not logged in, or user has no system privileges.
 */
export function useSystemRole(): SystemRole {
  return useAuthStore((state) => {
    const role = state.user?.systemRole;
    if (role === "root" || role === "admin") return role;
    return null;
  });
}

/**
 * True when the current user holds any system-admin privilege
 * (systemRole === 'admin' OR 'root').
 */
export function useIsSystemAdmin(): boolean {
  return useAuthStore((state) => {
    const role = state.user?.systemRole;
    return role === "admin" || role === "root";
  });
}

/**
 * True only when the current user is a system root admin
 * (systemRole === 'root'). Root has exclusive access to platform settings
 * and role assignment endpoints.
 */
export function useIsSystemRoot(): boolean {
  return useAuthStore((state) => state.user?.systemRole === "root");
}

/**
 * Returns auth loading state — used by guards to show a skeleton while the
 * /v1/me call is in-flight on first load (avoids flash-of-unauth, audit F5).
 */
export function useAuthIsLoading(): boolean {
  return useAuthStore((state) => state.isLoading || !state.isInitialized);
}
